import { useEffect } from 'react';
import { throttle } from 'lodash';
import { isEmpty } from 'lodash';
import { useQueryClient } from 'react-query';
import updateCollectedPhotoCard from '../../../components/api/mutations/legacy/updateCollectedPhotoCard';
import type { MemberType } from '../../types/ArtistType';
import type { PhotoCardType } from '../../types/PhotoCardType';

interface UseInvalidateQueriesProps {
  artistId: string;
  currentMember: MemberType | null;
}

type PhotoCardEventsInfinite = {
  pages?: Array<{
    meta?: {
      photoCardsInfo?: {
        collectedCount?: number;
        totalCount?: number;
      };
    };
    events?: Array<{
      photoCards?: Array<{
        photoCardId: string;
        collectedCount: number;
      }>;
    }>;
  }>;
};

// @TODO 뮤테이트함수로 분리해보기
export default function useInvalidateQueries({
  artistId,
  currentMember
}: UseInvalidateQueriesProps) {
  const queryClient = useQueryClient();
  // biome-ignore lint/correctness/useExhaustiveDependencies: 나중에 에딧페이지랑 합쳐지면 인벨리데이트 훅으로 빼기
  useEffect(() => {
    const receiveMessage = throttle((event) => {
      if (event.origin !== window.location.origin) return;
      if (isEmpty(event.data?.type)) return;

      if (event.data?.type === 'reported-new-photo-cards') {
        invalidateQueries();
      }

      if (event.data?.type === 'update-collected-count-on-artist-photo-cards') {
        // 1) 낙관적 업데이트: 현재 열려있는 photoCardEvents 페이지들의 캐시를 즉시 반영
        const payload = event.data?.params?.photoCards;

        if (payload && payload.length > 0) {
          const queries = queryClient.getQueryCache().findAll({
            queryKey: ['photoCardEvents', artistId]
          });

          const collectedMap = new Map(
            payload.map((p: { photoCardId: string; collectedCount: number }) => [
              p.photoCardId,
              p.collectedCount
            ])
          );

          queries.forEach((q) => {
            queryClient.setQueryData<PhotoCardEventsInfinite | undefined>(q.queryKey, (oldData) => {
              if (!oldData) return oldData;

              try {
                let totalDelta = 0;
                const updatedPages =
                  oldData.pages?.map((page) => {
                    const nextEvents = page.events?.map((evt) => {
                      if (!evt.photoCards?.length) return evt;
                      let changed = false;
                      const nextPCs = evt.photoCards.map((pc) => {
                        const nextCount = collectedMap.get(pc.photoCardId);
                        if (typeof nextCount === 'number' && nextCount !== pc.collectedCount) {
                          if ((pc.collectedCount || 0) === 0 && nextCount > 0) totalDelta += 1;
                          if ((pc.collectedCount || 0) > 0 && nextCount === 0) totalDelta -= 1;
                          changed = true;
                          return { ...pc, collectedCount: nextCount };
                        }
                        return pc;
                      });
                      return changed ? { ...evt, photoCards: nextPCs } : evt;
                    });
                    return { ...page, events: nextEvents };
                  }) ?? oldData.pages;

                const nextPages = updatedPages?.map((page) => {
                  if (!page.meta?.photoCardsInfo || totalDelta === 0) return page;
                  const current = page.meta.photoCardsInfo.collectedCount || 0;
                  return {
                    ...page,
                    meta: {
                      ...page.meta,
                      photoCardsInfo: {
                        ...page.meta.photoCardsInfo,
                        collectedCount: Math.max(0, current + totalDelta)
                      }
                    }
                  };
                });

                return { ...oldData, pages: nextPages };
              } catch (_e) {
                return oldData;
              }
            });
          });
        }

        // 2) 백그라운드 동기화: 실제 서버 데이터와 동기화
        invalidateQueries();
      }
    }, 1000);

    window.addEventListener('message', receiveMessage, false);
    return () => {
      window.removeEventListener('message', receiveMessage);
    };
  }, [queryClient, currentMember, artistId]);

  const invalidateQueries = () => {
    queryClient.invalidateQueries(['photoCardEvents', artistId]);
    queryClient.invalidateQueries(['collectedPhotoCardIds']);
  };

  const updateCollectedCount = async (photoCard: PhotoCardType, collectedCount: number) => {
    const response = await updateCollectedPhotoCard(photoCard.photoCardId, collectedCount);
    if (response.success) {
      invalidateQueries();
    }
  };

  return {
    updateCollectedCount
  };
}
